import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EntitiesList from './EntitiesList';
import EntityFrame from './EntityFrame';
import MainScreenFilters from './MainScreenFilters';
import { getPostsForDateRange } from '../../api/entities_api';
import { getRecentDateRange, RECENT_POSTS_DAYS } from '../../utils/recentPosts';
import { createErrorLogger } from '../../utils/errorHandler';

const logger = createErrorLogger('MainScreenContainer');

const REPORTED_ENTITY_IDS_KEY = 'awatch-reported-entity-ids';

/**
 * Ceiling on the bulk open. A week of entities can run long and every link
 * becomes a browser tab, so the newest MAX_RECENT_TABS are opened and the rest
 * are named in the status line.
 */
const MAX_RECENT_TABS = 100;

function loadReportedEntityIdsFromStorage() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(REPORTED_ENTITY_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function persistReportedEntityIds(ids) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(REPORTED_ENTITY_IDS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * MainScreenContainer - Container component for main screen content
 * Extracts main screen rendering logic from App.js
 * @param {Object} props
 */
const MainScreenContainer = ({
  entitiesError,
  refreshEntities,
  isOnMainScreen,
  selectedEntityId,
  updateUrlParams,
  entities,
  entitiesLoading,
  totalCount,
  currentPage,
  pageSize,
  handlePageChange,
  handlePageSizeChange,
  searchFailed,
  handleCloseEntityFrame,
  searchEntityId,
  onSearchChange,
  onSearchSubmit,
  searchLoading,
  urlParams,
  urlFilters,
  entitiesData,
  mainScreenLoading,
  setIsAlertPopupOpen,
}) => {
  const navigate = useNavigate();
  const [reportedEntityIds, setReportedEntityIds] = useState(loadReportedEntityIdsFromStorage);

  // Opening a post's tab is what marks it reported, one post or a whole week.
  const markEntityIdsReported = useCallback((entityIds) => {
    const ids = entityIds.map(String);
    setReportedEntityIds((prev) => {
      const next = [...prev, ...ids.filter((id) => !prev.includes(id))];
      if (next.length === prev.length) return prev;
      persistReportedEntityIds(next);
      return next;
    });
  }, []);

  const handleReportPostOpened = useCallback((entityId) => {
    markEntityIdsReported([entityId]);
  }, [markEntityIdsReported]);

  const [recentPostsLoading, setRecentPostsLoading] = useState(false);
  const [recentPosts, setRecentPosts] = useState(null);
  const [recentPostsStatus, setRecentPostsStatus] = useState('');
  const recentPostsRequest = useRef(null);

  /**
   * Collect the links up front, before anyone clicks. A pop-up blocker only
   * lets a page open tabs while the click that asked for them is still being
   * handled, so the click itself cannot afford to await this fetch — it has to
   * find the links already in hand.
   */
  const loadRecentPosts = useCallback(async () => {
    recentPostsRequest.current?.abort();
    const request = new AbortController();
    recentPostsRequest.current = request;

    setRecentPostsLoading(true);

    try {
      const { fromdate, todate } = getRecentDateRange(RECENT_POSTS_DAYS);
      const { posts, total } = await getPostsForDateRange({
        fromdate,
        todate,
        maxPosts: MAX_RECENT_TABS,
        signal: request.signal,
      });

      if (request.signal.aborted) return;
      setRecentPosts({ posts, total, skipped: Math.max(0, total - posts.length) });
    } catch (error) {
      if (error.name === 'AbortError') return;
      logger.error('Failed to load recent posts', error);
      setRecentPosts(null);
      setRecentPostsStatus(`Could not load the past ${RECENT_POSTS_DAYS} days. Click to try again.`);
    } finally {
      if (!request.signal.aborted) setRecentPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecentPosts();
    return () => recentPostsRequest.current?.abort();
  }, [loadRecentPosts]);

  // Synchronous by design: see loadRecentPosts. A click that failed to prefetch
  // retries instead, which is the only path that opens nothing.
  const handleRecentPostsClick = useCallback(() => {
    if (recentPostsLoading) return;

    // Only reachable when the prefetch failed, so this click is a retry.
    if (!recentPosts) {
      setRecentPostsStatus('');
      loadRecentPosts();
      return;
    }

    const { posts, skipped } = recentPosts;

    if (posts.length === 0) {
      setRecentPostsStatus(`No posts with a link in the past ${RECENT_POSTS_DAYS} days.`);
      return;
    }

    posts.forEach(({ url }) => window.open(url, '_blank', 'noopener,noreferrer'));
    markEntityIdsReported(posts.map((post) => post.entityId));

    setRecentPostsStatus(
      `Opening ${posts.length} post${posts.length === 1 ? '' : 's'}`
      + `${skipped > 0 ? ` (${skipped} more not opened)` : ''}`
      + '. If a tab is missing, allow pop-ups for this site.'
    );

    // Refresh in the background for the next reporting round. It leaves the
    // message above alone unless it fails.
    loadRecentPosts();
  }, [recentPostsLoading, recentPosts, loadRecentPosts, markEntityIdsReported]);

  if (entitiesError) {
    return (
      <div className="entities-error-container" style={{ textAlign: 'center', margin: '24px 0' }}>
        <div className="error-indicator" style={{ color: '#b91c1c', fontWeight: 'bold', marginBottom: 8 }}>
          {entitiesError}
        </div>
        <button
          className="retry-btn"
          style={{ padding: '8px 20px', fontSize: 16, background: '#fbbf24', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          onClick={refreshEntities}
        >
          Retry Loading Data
        </button>
      </div>
    );
  }

  const hasNoEntitySelected = !selectedEntityId;

  return (
    <div className={`main-content ${hasNoEntitySelected ? 'no-entity-selected' : ''}`}>
      <div className="database-screen">
        <div className="database-back-link" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>‹ Back</div>
        <section className="database-hero-panel">
          <div className="database-hero-top-row">
            <div className="database-hero-text">
              <h2>Our Database</h2>
            </div>
            <div className="database-hero-actions">
              <button
                type="button"
                className="database-open-recent-btn"
                onClick={handleRecentPostsClick}
                disabled={recentPostsLoading}
                title={`Open every post from the past ${RECENT_POSTS_DAYS} days in its own tab, ready to report`}
              >
                {`Report All Posts from the past ${RECENT_POSTS_DAYS} days`}
                {recentPosts?.posts.length > 0 ? ` (${recentPosts.posts.length})` : ''}
              </button>
              <button type="button" className="database-alert-btn" onClick={() => setIsAlertPopupOpen(true)}>Alert Us</button>
            </div>
          </div>
          {recentPostsStatus && (
            <div className="database-hero-status" role="status">{recentPostsStatus}</div>
          )}
          <div className="database-hero-text">
            <p>A searchable well of content verified and analyzed by CyberWell for antisemitism. Tap on the summary for the full post.</p>
          </div>
          <div className="database-search-row">
            <div className="search-input-group" id="header-search-input-group">
              <svg className="search-input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <circle cx="7.5" cy="7.5" r="5.75" stroke="#303030" strokeWidth="1.5"/>
                <line x1="12.03" y1="12.03" x2="16" y2="16" stroke="#303030" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search by platform, IHRA or description..."
                value={searchEntityId}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSearchSubmit();
                }}
                className="search-input"
                disabled={searchLoading}
              />
            </div>
            <MainScreenFilters
              urlParams={urlParams}
              urlFilters={urlFilters}
              updateUrlParams={updateUrlParams}
              entitiesData={entitiesData}
              entitiesLoading={mainScreenLoading}
              onSearchChange={onSearchChange}
            />
            <div className="database-status-pills">
              <span
                className={`database-pill database-pill-removed${urlFilters?.IsRemoved?.in?.includes(true) ? ' active' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const current = urlFilters?.IsRemoved?.in || [];
                  const newIn = current.includes(true)
                    ? current.filter(v => v !== true)
                    : [...current, true];
                  const newFilters = { ...(urlFilters || {}) };
                  if (newIn.length === 0) {
                    delete newFilters.IsRemoved;
                  } else {
                    newFilters.IsRemoved = { in: newIn };
                  }
                  updateUrlParams({ filters: newFilters, page: 1 });
                }}
              >Removed</span>
              <span
                className={`database-pill database-pill-not-removed${urlFilters?.IsRemoved?.in?.includes(false) ? ' active' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const current = urlFilters?.IsRemoved?.in || [];
                  const newIn = current.includes(false)
                    ? current.filter(v => v !== false)
                    : [...current, false];
                  const newFilters = { ...(urlFilters || {}) };
                  if (newIn.length === 0) {
                    delete newFilters.IsRemoved;
                  } else {
                    newFilters.IsRemoved = { in: newIn };
                  }
                  updateUrlParams({ filters: newFilters, page: 1 });
                }}
              >Not Removed</span>
            </div>
          </div>
          <div className="database-results-meta">
            <span className="database-total-posts-label">Total posts: </span>
            <span className="database-total-posts-value">{totalCount.toLocaleString()}</span>
          </div>
        </section>

        <div className="database-layout">
          <EntitiesList
            onSelectEntity={(entityId) => {
              if (isOnMainScreen) {
                updateUrlParams({ entity: entityId });
              }
            }}
            selectedEntityId={selectedEntityId}
            entities={entities}
            entitiesLoading={entitiesLoading}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRefresh={refreshEntities}
            reportedEntityIds={reportedEntityIds}
          />
          <EntityFrame
            entityId={selectedEntityId}
            searchFailed={searchFailed}
            hasNoResults={entities.length === 0 && totalCount === 0 && !entitiesLoading}
            onClose={handleCloseEntityFrame}
            entities={entities}
            entitiesLoading={entitiesLoading}
            onSelectEntity={(entityId) => {
              if (isOnMainScreen) {
                updateUrlParams({ entity: entityId });
              }
            }}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            handlePageChange={handlePageChange}
            onReportPost={handleReportPostOpened}
          />
        </div>
      </div>
    </div>
  );
};

export default MainScreenContainer;

